import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  FunctionField,
  EditButton,
  ReferenceField,
  TextInput,
  TextField,
  DateField,
  useEditContext,
  usePermissions,
  useTranslate,
} from 'react-admin';

import EmptyList from '../components/misc/EmptyList';
import SelectUserByEmailInput from '../components/form/SelectUserByEmailInput';
import ResourceListTabs from '../components/misc/ResourceListTabs';
import { ApplicationDatagrid } from './eventCreationApplications';
import { usePendingApplicationCount } from '../hooks/usePendingApplications';
import {
  SALE_AGENT_APPLICATION_FILTERS,
  TABBED_LIST_SX,
  TabbedListActionsPlaceholder,
} from '../components/misc/tabbedListLayout';

const entity = 'saleAgents';

export const SaleAgentList = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const [searchParams] = useSearchParams();
  const isApplications = searchParams.get('tab') === 'applications';
  const pendingApplications = usePendingApplicationCount('salesAgent');

  const canViewApplications =
    permissions?.crole === 'admin' || permissions?.crole === 'operator';

  const saleAgentTabs = [
    { id: 'list', label: translate('saleAgents.tab_list', { _: 'Satış Temsilcileri' }) },
    { id: 'applications', label: translate('saleAgents.tab_applications', { _: 'Başvurular' }), badge: pendingApplications },
  ];
  const showingApplications = isApplications && canViewApplications;

  return (
    <List
      {...props}
      resource={showingApplications ? 'eventCreationApplications' : entity}
      filter={
        showingApplications
          ? { companyType: 'salesAgent', isChecked: false }
          : undefined
      }
      empty={
        <EmptyList
          entity={showingApplications ? 'eventCreationApplications' : entity}
        />
      }
      title={showingApplications ? translate('saleAgents.title_applications', { _: 'Satış Temsilcisi Başvuruları' }) : translate('saleAgents.title', { _: 'Satış Temsilcileri' })}
      filters={
        showingApplications ? SALE_AGENT_APPLICATION_FILTERS : undefined
      }
      actions={showingApplications ? <TabbedListActionsPlaceholder /> : undefined}
      exporter={false}
      sort={
        showingApplications ? { field: 'createdate', order: 'DESC' } : undefined
      }
      perPage={showingApplications ? 25 : 100}
      sx={TABBED_LIST_SX}
    >
      {canViewApplications ? (
        <ResourceListTabs
          activeTab={showingApplications ? 'applications' : 'list'}
          tabs={saleAgentTabs}
        />
      ) : null}
      {showingApplications ? (
        <ApplicationDatagrid showSlug={false} />
      ) : (
        <Datagrid>
          <TextField source="name" label={translate('saleAgents.name', { _: 'İsim' })} />
          <ReferenceField source="user" reference="users" label={translate('saleAgents.user', { _: 'Kullanıcı' })} link={false}>
            <FunctionField
              render={(r) =>
                `${r.name || ''} ${r.surname || ''}`.trim() || r.email
              }
            />
          </ReferenceField>
          <ReferenceField source="user" reference="users" label={translate('saleAgents.email', { _: 'E-posta' })} link={false}>
            <TextField source="email" />
          </ReferenceField>
          <ReferenceField source="user" reference="users" label={translate('saleAgents.mobile', { _: 'Telefon' })} link={false}>
            <TextField source="mobile" />
          </ReferenceField>
          <TextField source="notes" label={translate('saleAgents.notes', { _: 'Notlar' })} />
          <DateField source="createdate" label={translate('saleAgents.created', { _: 'Oluşturulma Tarihi' })} showTime />
          <EditButton />
        </Datagrid>
      )}
    </List>
  );
};

export const SaleAgentCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create title={translate('saleAgents.new_agent', { _: 'Yeni Satış Temsilcisi' })} {...props} redirect="list">
      <SimpleForm redirect="list">
        <h3>{translate('saleAgents.user_section', { _: 'Kullanıcı' })}</h3>
        <SelectUserByEmailInput source="user" required />

        <h3>{translate('saleAgents.details_section', { _: 'Detaylar' })}</h3>
        <TextInput source="notes" label={translate('saleAgents.notes', { _: 'Notlar' })} fullWidth multiline rows={3} />
      </SimpleForm>
    </Create>
  );
};

export const SaleAgentEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit title={translate('saleAgents.edit_agent', { _: 'Satış Temsilcisini Düzenle' })} {...props}>
      <SimpleForm redirect={`/${entity}`}>
        <EditForm />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = () => {
  const translate = useTranslate();
  const { record, isLoading } = useEditContext();
  if (isLoading) return null;

  return (
    <React.Fragment>
      <h3>{translate('saleAgents.user_section', { _: 'Kullanıcı' })}</h3>
      <SelectUserByEmailInput source="user" record={record} required />
      <h3>{translate('saleAgents.details_section', { _: 'Detaylar' })}</h3>
      <TextInput source="notes" label={translate('saleAgents.notes', { _: 'Notlar' })} fullWidth multiline rows={3} />
    </React.Fragment>
  );
};
